/**
 * Share Target Page
 * Handles content shared TO the app from other apps (Walmart, Amazon, etc.)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  TextField
} from '@mui/material';
import {
  ShoppingCart,
  Close,
  Add
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';

export default function ShareTargetPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { addShoppingItem } = useFamily();
  
  const [loading, setLoading] = useState(true);
  const [sharedData, setSharedData] = useState(null);
  const [itemName, setItemName] = useState('');
  const [itemNotes, setItemNotes] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    processSharedContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processSharedContent = async () => {
    try {
      // Get shared data from URL params
      const title = searchParams.get('title') || '';
      const text = searchParams.get('text') || '';
      const url = searchParams.get('url') || '';

      // Try to extract item name from shared content
      let extractedName = title || text.split('\n')[0] || '';
      
      // Clean up common patterns
      extractedName = extractedName
        .replace(/^(Buy|Get|Add)\s+/i, '')
        .replace(/\s+[-–—]\s+.*$/, '') // Remove after dash
        .trim();

      setSharedData({ title, text, url });
      setItemName(extractedName);
      setItemNotes(text !== extractedName ? text : '');
      
      setLoading(false);
    } catch (error) {
      console.error('Error processing shared content:', error);
      setError('Could not process shared content');
      setLoading(false);
    }
  };

  const handleAddToShoppingList = async () => {
    if (!itemName.trim()) {
      setError('Please enter an item name');
      return;
    }

    setLoading(true);

    try {
      await addShoppingItem({
        name: itemName.trim(),
        notes: itemNotes.trim(),
        approved: false,
        requestedBy: currentUser?.uid,
        category: 'Other',
        addedAt: new Date().toISOString()
      });

      console.log('✅ Item added to shopping list from share');
      
      // Navigate to shopping list
      navigate('/shopping');
    } catch (error) {
      console.error('Error adding item:', error);
      setError('Failed to add item to shopping list');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/shopping');
  };

  if (!currentUser) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info">
          Please log in to add items to your shopping list.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/auth')}
          sx={{ mt: 2 }}
        >
          Log In
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <ShoppingCart color="primary" />
              <Typography variant="h5">
                Add to Shopping List
              </Typography>
            </Box>
            <Button
              size="small"
              startIcon={<Close />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Box>

          {loading && !sharedData ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : (
            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleAddToShoppingList(); }}>
              <TextField
                label="Item Name"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                fullWidth
                required
                autoFocus
                margin="normal"
                placeholder="e.g., Milk, Bread, Eggs"
              />

              <TextField
                label="Notes (optional)"
                value={itemNotes}
                onChange={(e) => setItemNotes(e.target.value)}
                fullWidth
                multiline
                rows={3}
                margin="normal"
                placeholder="Any additional details..."
              />

              {sharedData?.url && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    Shared from: {sharedData.url}
                  </Typography>
                </Alert>
              )}

              <Box display="flex" gap={2} mt={3}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  startIcon={loading ? <CircularProgress size={20} /> : <Add />}
                  disabled={loading || !itemName.trim()}
                >
                  {loading ? 'Adding...' : 'Add to List'}
                </Button>
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" mt={2} textAlign="center">
                💡 Tip: You can share items from any app to Earn-E!
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

